/**
 * Runtime theme. Dark is the default color scheme; light is a token-only
 * variant. Accent is lavender by default and ember when opted in. Neither
 * requires a reload — both are attributes on <html> plus the variables in
 * src/styles.css. The inline script in index.html applies the stored choice
 * before first paint.
 */
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
	type AccentTheme,
	applyAccent,
	applyMode,
	getAccent,
	getMode,
	setAccent as persistAccent,
	setMode as persistMode,
	readStoredTheme,
	type ThemeMode,
} from "@/lib/theme";

export type { AccentTheme, ThemeMode };

type ThemeContextValue = {
	mode: ThemeMode;
	accent: AccentTheme;
	setMode: (mode: ThemeMode) => void;
	setAccent: (accent: AccentTheme) => void;
	/** Quick flip between light and dark (collapses "system" to the current side). */
	toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [mode, setModeState] = useState<ThemeMode>(() => readStoredTheme().mode);
	const [accent, setAccentState] = useState<AccentTheme>(() => readStoredTheme().accent);
	const [systemLight, setSystemLight] = useState(() =>
		typeof window === "undefined"
			? false
			: window.matchMedia("(prefers-color-scheme: light)").matches,
	);

	useEffect(() => {
		const mq = window.matchMedia("(prefers-color-scheme: light)");
		const onChange = () => setSystemLight(mq.matches);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);

	useEffect(() => {
		applyMode(mode === "system" ? (systemLight ? "light" : "dark") : mode);
	}, [mode, systemLight]);

	useEffect(() => {
		applyAccent(accent);
	}, [accent]);

	const value = useMemo<ThemeContextValue>(() => {
		const setMode = (next: ThemeMode) => {
			persistMode(next);
			setModeState(next);
		};
		const setAccent = (next: AccentTheme) => {
			persistAccent(next);
			setAccentState(next);
		};
		const resolvedLight = mode === "system" ? systemLight : mode === "light";
		return {
			mode,
			accent,
			setMode,
			setAccent,
			toggle: () => setMode(resolvedLight ? "dark" : "light"),
		};
	}, [mode, accent, systemLight]);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
	return ctx;
}

/** Re-read helpers for non-React callers. Prefer `useTheme` inside the tree. */
export { getAccent, getMode };
